import { useEffect, useState } from "react";

/**
 * The block/tower list an apartment hub is configured with.
 *
 * Every place a customer can set or confirm their block reads it through this
 * hook, so the address form, the checkout modal and the confirmation popup can
 * never offer different choices for the same hub — which is the whole point:
 * consistency comes from everyone picking off one identical list.
 *
 * Results are memoised at module scope. A hub's blocks change roughly never,
 * but the popup and the form can mount within a second of each other, and
 * refetching the same list on every mount is a visible flash of empty buttons.
 */
const cache = new Map(); // hubId -> { blocks, hubType, hubName }
const inFlight = new Map(); // hubId -> Promise

const API_BASE = "https://dailydish.in/api";

async function fetchHubBlocks(hubId) {
  if (cache.has(hubId)) return cache.get(hubId);
  if (inFlight.has(hubId)) return inFlight.get(hubId);

  const request = (async () => {
    try {
      const response = await fetch(`${API_BASE}/Hub/hubs/${hubId}/blocks`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const result = {
        // The endpoint returns [{ block, addressCount }] so the admin screen can
        // warn before deleting a block people live in. Customers only need the
        // names.
        blocks: (data.blocks || []).map((entry) =>
          typeof entry === "string" ? entry : entry.block,
        ),
        hubType: data.hubType || null,
        hubName: data.hubName || "",
      };
      cache.set(hubId, result);
      return result;
    } catch (error) {
      // Never block address saving on this. With no list, BlockPicker falls
      // back to a plain text input and the server skips block validation — the
      // customer can still complete their address.
      console.warn("[useHubBlocks] could not load blocks:", error.message);
      return { blocks: [], hubType: null, hubName: "" };
    } finally {
      inFlight.delete(hubId);
    }
  })();

  inFlight.set(hubId, request);
  return request;
}

export function useHubBlocks(hubId) {
  const [state, setState] = useState(() =>
    hubId && cache.has(hubId)
      ? { ...cache.get(hubId), loading: false }
      : { blocks: [], hubType: null, hubName: "", loading: Boolean(hubId) },
  );

  useEffect(() => {
    if (!hubId) {
      setState({ blocks: [], hubType: null, hubName: "", loading: false });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    fetchHubBlocks(hubId).then((result) => {
      if (!cancelled) setState({ ...result, loading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [hubId]);

  return state;
}

/** Drop a cached hub, e.g. after an admin edits its block list. */
export function clearHubBlocksCache(hubId) {
  if (hubId) cache.delete(hubId);
  else cache.clear();
}

export default useHubBlocks;
